import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { packCustomerApi, FP510 } from '../../../services/packCustomerApi';

export type Mode = 'browse' | 'add' | 'edit';

export function usePackCustomerForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FP510[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [recipient, setRecipient] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await packCustomerApi.list(1, 500);
      setRecords(res.data || []);
      return res.data || [];
    } catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  const syncFrom = useCallback((r: FP510) => {
    setCode(r.fp510_01); setName(r.fp510_10);
    setRecipient(r.fp510_11); setPhone(r.fp510_12);
    setAddress(r.fp510_13);
  }, []);

  useEffect(() => { if (current && mode === 'browse') syncFrom(current); }, [current, mode, syncFrom]);
  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => {
    setMode('add'); setCode(''); setName('');
    setRecipient(''); setPhone(''); setAddress('');
  };
  const startEdit = () => { if (current) { setMode('edit'); syncFrom(current); } };
  const cancel = () => { setMode('browse'); if (current) syncFrom(current); };

  const save = async () => {
    const payload = {
      fp510_10: name, fp510_11: recipient,
      fp510_12: phone, fp510_13: address,
    };
    try {
      if (mode === 'add') {
        await packCustomerApi.create({ fp510_01: code, ...payload });
        message.success('新增成功');
      } else {
        await packCustomerApi.update(current!.fp510_01, payload);
        message.success('修改成功');
      }
      const data = await loadRecords();
      const tc = mode === 'add' ? code : current!.fp510_01;
      const idx = data.findIndex((r: FP510) => r.fp510_01 === tc);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await packCustomerApi.delete(current.fp510_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1));
      else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    const term = t.trim().toUpperCase();
    if (!term) return;

    // Exact match
    const exactIdx = records.findIndex(r => r.fp510_01.toUpperCase() === term);
    if (exactIdx >= 0) {
      setCurrentIdx(exactIdx);
      return;
    }

    // Near match (starts with or contains)
    const nearIdx = records.findIndex(r =>
      r.fp510_01.toUpperCase().startsWith(term) ||
      r.fp510_10.toUpperCase().includes(term)
    );
    if (nearIdx >= 0) {
      setCurrentIdx(nearIdx);
      message.info(`找到近似資料：${records[nearIdx].fp510_01}`);
      return;
    }

    // Not found
    message.warning('查無符合資料');
  };

  return {
    mode, isEditing, loading, records, current, currentIdx,
    code, setCode, name, setName, recipient, setRecipient,
    phone, setPhone, address, setAddress,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, quickSearch,
  };
}
